<template>
  <div>
  <b-modal
    :active.sync="show"
    trap-focus
    aria-role="dialog"
    scroll="keep"
    :width="1200"
    aria-modal
    has-modal-card
    @close="closeModal"
  >
    <ValidationObserver ref="observer" v-slot="{ passes }">
      <form @submit.prevent="passes(save)">
        <div class="modal-card" style="width: 1200px; min-height: 600px">
          <header class="modal-card-head">
            <p class="modal-card-title">Copy Sequence to Multiple Slots</p>
          </header>
          <section class="modal-card-body" style="min-height: 700px;">
            <div class="columns">
              <!-- Left side: Copy destinations list -->
              <div class="column is-6">
                <div class="destinations-list" style="max-height: 600px; overflow-y: auto;">
                  <!-- Primary Destination -->
                  <div class="box destination-item" style="background: #e8f4f8; border-left: 4px solid #3273dc;">
                    <div class="destination-header">
                      <span class="tag is-primary has-margin-right-10">Primary</span>
                      <strong>First Copy Destination</strong>
                    </div>
                    <BaseDateTimePicker
                      v-model="startdate"
                      label="Date/Time"
                      class="has-margin-top-10"
                    />

                    <!-- Primary title change option -->
                    <div class="title-change-section has-margin-top-10">
                      <b-field>
                        <b-checkbox v-model="primaryChangeTitle">
                          Change title for this copy
                        </b-checkbox>
                      </b-field>
                      <div v-if="primaryChangeTitle" class="has-margin-top-10">
                        <b-field label="New Title" label-position="on-border">
                          <b-autocomplete
                            v-model="primaryTitleSearch"
                            placeholder="Select a title"
                            :data="getFilteredTitles(primaryTitleSearch)"
                            field="title"
                            expanded
                            size="is-small"
                            :open-on-focus="true"
                            append-to-body
                            @select="val => onPrimaryTitleSelect(val)"
                          />
                        </b-field>
                        <b-field
                          v-if="primarySelectedTitle && primarySelectedTitle.media_type === 'series'"
                          label="Episode"
                          label-position="on-border"
                        >
                          <b-select v-model="primarySelectedEpisodeId" expanded size="is-small">
                            <option
                              v-for="episode in primaryEpisodes"
                              :key="episode.id"
                              :value="episode.id"
                            >
                              {{ episodeStatusDot(episode) }}
                              {{ episode.episode_number }}
                              {{ episode.title ? `- ${episode.title}` : '' }}
                            </option>
                          </b-select>
                        </b-field>
                      </div>
                    </div>
                  </div>

                  <!-- Additional Destinations -->
                  <div
                    v-for="(slot, index) in additionalSlots"
                    :key="index"
                    class="box destination-item"
                    style="background: #f9f9f9;"
                  >
                    <div class="destination-header" style="display: flex; justify-content: space-between; align-items: center;">
                      <div>
                        <span class="tag is-info has-margin-right-10">Copy {{ index + 2 }}</span>
                        <strong>{{ formatDateTime(slot.start_time) }}</strong>
                      </div>
                      <button
                        type="button"
                        class="delete is-small"
                        @click="removeAdditionalSlot(index)"
                      ></button>
                    </div>

                    <!-- Per-slot title change option -->
                    <div class="title-change-section has-margin-top-10">
                      <b-field>
                        <b-checkbox v-model="slot.changeTitle">
                          Change title for this copy
                        </b-checkbox>
                      </b-field>
                      <div v-if="slot.changeTitle" class="has-margin-top-10">
                        <b-field label="New Title" label-position="on-border">
                          <b-autocomplete
                            v-model="slot.titleSearch"
                            placeholder="Select a title"
                            :data="getFilteredTitles(slot.titleSearch)"
                            field="title"
                            expanded
                            size="is-small"
                            :open-on-focus="true"
                            append-to-body
                            @select="val => onSlotTitleSelect(slot, val)"
                          />
                        </b-field>
                        <b-field
                          v-if="slot.selectedTitle && slot.selectedTitle.media_type === 'series'"
                          label="Episode"
                          label-position="on-border"
                        >
                          <b-select v-model="slot.selectedEpisodeId" expanded size="is-small">
                            <option
                              v-for="episode in slot.episodes"
                              :key="episode.id"
                              :value="episode.id"
                            >
                              {{ episodeStatusDot(episode) }}
                              {{ episode.episode_number }}
                              {{ episode.title ? `- ${episode.title}` : '' }}
                            </option>
                          </b-select>
                        </b-field>
                      </div>
                    </div>
                  </div>

                  <!-- Empty state -->
                  <div v-if="additionalSlots.length === 0" class="has-text-grey has-text-centered has-padding-15">
                    <p>Use the slot picker on the right to add more copy destinations.</p>
                  </div>
                </div>
              </div>

              <!-- Right side: Slot picker -->
              <div class="column is-6">
                <div class="box" style="background: #f9f9f9; min-height: 550px;">
                  <p class="has-text-weight-bold has-margin-bottom-10">
                    Add Copy Destinations
                    <span v-if="additionalSlots.length > 0" class="tag is-info">
                      {{ additionalSlots.length }} added
                    </span>
                  </p>
                  <p class="help has-margin-bottom-10">
                    Click on time slots to add more copy destinations.
                  </p>
                  <SlotPickerCalendar
                    v-if="channelId"
                    :channel-id="channelId"
                    :duration="30"
                    :initial-start-date="startdate"
                    :episode-count="1"
                    @slots-changed="onSlotsChanged"
                  />
                </div>
              </div>
            </div>

            <!-- Summary -->
            <div v-if="totalCopies > 0" class="notification is-info is-light has-margin-top-15">
              <p class="has-text-weight-bold">
                Summary: {{ totalCopies }} cop{{ totalCopies !== 1 ? 'ies' : 'y' }} will be created
              </p>
              <ul class="has-margin-top-10" style="list-style: none; margin-left: 0;">
                <li>
                  ✓ <strong>Primary:</strong> {{ formatDateTime(startdate) }}
                  <span v-if="primaryChangeTitle && primarySelectedTitle" class="has-text-success">
                    → {{ primarySelectedTitle.title }}
                  </span>
                </li>
                <li v-for="(slot, index) in additionalSlots" :key="index">
                  ✓ <strong>Copy {{ index + 2 }}:</strong> {{ formatDateTime(slot.start_time) }}
                  <span v-if="slot.changeTitle && slot.selectedTitle" class="has-text-success">
                    → {{ slot.selectedTitle.title }}
                  </span>
                </li>
              </ul>
            </div>
          </section>
          <footer class="modal-card-foot" style="display: block">
            <button class="button" type="button" @click="closeModal">
              Cancel
            </button>
            <button
              class="button is-primary"
              type="button"
              :disabled="loading || !canSave"
              @click="save"
            >
              <span v-if="loading">{{ loadingMessage }}</span>
              <span v-else>
                Copy to {{ totalCopies }} Destination{{ totalCopies !== 1 ? 's' : '' }}
              </span>
            </button>
          </footer>
        </div>
      </form>
    </ValidationObserver>
  </b-modal>

  <!-- Clash Dialog Modal -->
  <b-modal :active.sync="showClashModal" has-modal-card trap-focus aria-role="dialog" aria-modal :can-cancel="['escape']">
    <div class="modal-card" style="max-width: 600px; margin: 0 auto;">
      <header class="modal-card-head" style="background-color: #ffdd57;">
        <p class="modal-card-title">
          <b-icon icon="alert-circle" type="is-warning" style="margin-right: 8px;" />
          Schedule Clash Detected
        </p>
      </header>
      <section class="modal-card-body">
        <div v-if="clashDialogData.currentDest" style="margin-bottom: 10px;">
          <strong>Destination {{ clashDialogData.currentDest }} of {{ clashDialogData.totalDests }}:</strong>
        </div>
        <div v-for="(clash, index) in clashDialogData.clashes" :key="index" style="margin-bottom: 15px;">
          <div><strong>New Item {{ index + 1 }}:</strong></div>
          <div>{{ formatClashTime(clash.newItem.start_time) }} - {{ formatClashTimeOnly(clash.newItem.end_time) }}</div>
          <div style="margin-top: 8px;"><strong>Conflicts with:</strong></div>
          <div v-for="(conflict, ci) in clash.conflicts" :key="ci" style="margin-left: 15px; color: #d9534f;">
            &bull; {{ conflict.title }}<br/>
            &nbsp;&nbsp;{{ formatClashTime(conflict.start_time) }} - {{ formatClashTimeOnly(conflict.end_time) }}
          </div>
        </div>
        <div v-if="clashDialogData.firstClashIndex > 0" style="margin-top: 15px; padding: 10px; background: #f5f5f5; border-radius: 4px;">
          <strong>{{ clashDialogData.firstClashIndex }}</strong> of <strong>{{ clashDialogData.totalItems }}</strong> items can be scheduled before the first clash.
        </div>
        <div v-else-if="clashDialogData.firstClashIndex === 0" style="margin-top: 15px; padding: 10px; background: #fff3cd; border-radius: 4px;">
          The first item already has a clash. No items can be scheduled before it.
        </div>
      </section>
      <footer class="modal-card-foot" style="justify-content: flex-end; gap: 8px;">
        <b-button @click="onClashCancel">Cancel</b-button>
        <b-button
          v-if="clashDialogData.firstClashIndex > 0"
          type="is-info"
          @click="onClashScheduleUpTo"
        >
          Schedule {{ clashDialogData.firstClashIndex }} item{{ clashDialogData.firstClashIndex > 1 ? 's' : '' }}
        </b-button>
        <b-button type="is-warning" @click="onClashOverwrite">
          Overwrite
        </b-button>
      </footer>
    </div>
  </b-modal>
  </div>
</template>

<script>
import ScheduleApi from '@/api/schedules'
import MediaChannelApi from '@/api/mediaChannels'
import EpisodesApi from '@/api/episodes'
import { episodeStatusDot } from '@/utils/episodeStatus'
import { sortBy } from 'lodash'
import format from 'date-fns/format'
import BaseDateTimePicker from '@/components/BaseDateTimePicker'
import SlotPickerCalendar from '@/components/SlotPickerCalendar'

export default {
  name: 'CopySequenceModal',
  components: {
    BaseDateTimePicker,
    SlotPickerCalendar
  },
  props: {
    show: {
      type: Boolean,
      default: false
    },
    repeatsId: {
      type: Number,
      default: null
    },
    channelId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      loadingMessage: 'Copying...',
      initialized: false,
      showModal: this.show,
      startdate: new Date(),
      titles: [],
      // Primary destination title change
      primaryChangeTitle: false,
      primaryTitleSearch: '',
      primarySelectedTitle: null,
      primaryEpisodes: [],
      primarySelectedEpisodeId: null,
      // Additional slots with per-slot title change
      additionalSlots: [],
      // Clash dialog
      showClashModal: false,
      clashDialogData: { clashes: [], firstClashIndex: -1, totalItems: 0, currentDest: 0, totalDests: 0 },
      clashResolve: null
    }
  },
  computed: {
    totalCopies() {
      return 1 + this.additionalSlots.length
    },
    canSave() {
      // Check primary - if changing title, must have selected one
      if (this.primaryChangeTitle && !this.primarySelectedTitle) {
        return false
      }
      // Check each additional slot
      for (const slot of this.additionalSlots) {
        if (slot.changeTitle && !slot.selectedTitle) {
          return false
        }
      }
      return true
    }
  },
  watch: {
    async primarySelectedTitle(newVal) {
      if (newVal && newVal.media_type === 'series') {
        let episodes = await EpisodesApi.getAllByMediaId(newVal.id)
        episodes.forEach(episode => {
          episode.episode_number = parseInt(episode.episode_number, 10)
        })
        this.primaryEpisodes = sortBy(episodes, 'episode_number')
        if (this.primaryEpisodes.length > 0) {
          this.primarySelectedEpisodeId = this.primaryEpisodes[0].id
        }
      } else {
        this.primaryEpisodes = []
        this.primarySelectedEpisodeId = null
      }
    }
  },
  async created() {
    // Load available titles for the channel
    let titles = await MediaChannelApi.getByChannelId(this.channelId)
    titles.forEach(item => {
      let mediadata = null
      if (item.mediadata) {
        mediadata = JSON.parse(item.mediadata)
      }
      item.title =
        item.title +
        (item.media_type === 'series' && mediadata && mediadata.seriesNo
          ? ` S${mediadata.seriesNo}`
          : '') +
        ` - ${item.media_type.charAt(0).toUpperCase() + item.media_type.slice(1)}`
    })
    this.titles = titles
    this.initialized = true
  },
  methods: {
    episodeStatusDot,
    getFilteredTitles(searchTerm) {
      if (!this.titles || this.titles.length === 0) {
        return []
      }
      if (searchTerm && searchTerm.length > 0) {
        const filter = searchTerm.toLowerCase()
        return this.titles.filter(item =>
          item.title && item.title.toLowerCase().includes(filter)
        )
      }
      return this.titles
    },
    onPrimaryTitleSelect(value) {
      if (value && value.id) {
        this.primarySelectedTitle = value
      }
    },
    async onSlotTitleSelect(slot, value) {
      if (value && value.id) {
        this.$set(slot, 'selectedTitle', value)
        // Load episodes for series
        if (value.media_type === 'series') {
          let episodes = await EpisodesApi.getAllByMediaId(value.id)
          episodes.forEach(episode => {
            episode.episode_number = parseInt(episode.episode_number, 10)
          })
          this.$set(slot, 'episodes', sortBy(episodes, 'episode_number'))
          if (slot.episodes.length > 0) {
            this.$set(slot, 'selectedEpisodeId', slot.episodes[0].id)
          }
        } else {
          this.$set(slot, 'episodes', [])
          this.$set(slot, 'selectedEpisodeId', null)
        }
      }
    },
    onSlotsChanged(slots) {
      // Filter out the primary start date from additional slots
      const primaryStart = this.startdate ? this.startdate.getTime() : null

      // Keep existing slot data (title changes) when slots update
      const existingSlotData = new Map()
      this.additionalSlots.forEach(slot => {
        const key = slot.start_time ? slot.start_time.getTime() : null
        if (key) {
          existingSlotData.set(key, {
            changeTitle: slot.changeTitle,
            titleSearch: slot.titleSearch,
            selectedTitle: slot.selectedTitle,
            episodes: slot.episodes,
            selectedEpisodeId: slot.selectedEpisodeId
          })
        }
      })

      this.additionalSlots = slots
        .filter(slot => {
          if (!primaryStart) return true
          const slotStart = slot.start_time ? slot.start_time.getTime() : null
          return slotStart !== primaryStart
        })
        .map(slot => {
          const slotStart = slot.start_time ? slot.start_time.getTime() : null
          const existing = slotStart ? existingSlotData.get(slotStart) : null
          return {
            start_time: slot.start_time,
            end_time: slot.end_time,
            changeTitle: existing ? existing.changeTitle : false,
            titleSearch: existing ? existing.titleSearch : '',
            selectedTitle: existing ? existing.selectedTitle : null,
            episodes: existing ? existing.episodes : [],
            selectedEpisodeId: existing ? existing.selectedEpisodeId : null
          }
        })
    },
    removeAdditionalSlot(index) {
      this.additionalSlots.splice(index, 1)
    },
    async copyToDestination(dest, extraFlags = {}) {
      return await ScheduleApi.copySequence({
        repeats_id: this.repeatsId,
        start_time: dest.start_time,
        ...extraFlags
      })
    },
    showClashDialogPromise(clashData, currentDest, totalDests) {
      return new Promise((resolve) => {
        this.clashDialogData = {
          clashes: clashData.clashes || [],
          firstClashIndex: clashData.firstClashIndex != null ? clashData.firstClashIndex : -1,
          totalItems: clashData.totalItems || 0,
          currentDest,
          totalDests
        }
        this.clashResolve = resolve
        this.showClashModal = true
      })
    },
    onClashCancel() {
      this.showClashModal = false
      if (this.clashResolve) this.clashResolve('cancel')
    },
    onClashScheduleUpTo() {
      this.showClashModal = false
      if (this.clashResolve) this.clashResolve('schedule-up-to')
    },
    onClashOverwrite() {
      this.showClashModal = false
      if (this.clashResolve) this.clashResolve('overwrite')
    },
    formatClashTime(dateStr) {
      if (!dateStr) return ''
      return format(new Date(dateStr), 'EEE MMM d @ HH:mm')
    },
    formatClashTimeOnly(dateStr) {
      if (!dateStr) return ''
      return format(new Date(dateStr), 'HH:mm')
    },
    async save() {
      this.loading = true
      this.loadingMessage = 'Copying...'

      try {
        // Build array of all destinations with their title changes
        const destinations = [
          {
            start_time: new Date(this.startdate),
            changeTitle: this.primaryChangeTitle,
            selectedTitle: this.primarySelectedTitle,
            selectedEpisodeId: this.primarySelectedEpisodeId
          },
          ...this.additionalSlots.map(slot => ({
            start_time: new Date(slot.start_time),
            changeTitle: slot.changeTitle,
            selectedTitle: slot.selectedTitle,
            selectedEpisodeId: slot.selectedEpisodeId
          }))
        ]

        let copiedCount = 0

        // Copy to each destination
        for (let i = 0; i < destinations.length; i++) {
          const dest = destinations[i]
          this.loadingMessage = `Copying to destination ${i + 1} of ${destinations.length}...`

          let result = null
          try {
            result = await this.copyToDestination(dest)
          } catch (error) {
            if (error && error.status === 409 && error.data && error.data.clashes) {
              // Show clash dialog and wait for user decision
              const decision = await this.showClashDialogPromise(error.data, i + 1, destinations.length)

              if (decision === 'cancel') {
                break // Stop processing remaining destinations
              } else if (decision === 'overwrite') {
                result = await this.copyToDestination(dest, { copy: true })
              } else if (decision === 'schedule-up-to') {
                result = await this.copyToDestination(dest, { scheduleUpToClash: true })
              }
            } else {
              throw error // Re-throw non-clash errors
            }
          }

          // If this destination has a title change, apply it
          if (result && dest.changeTitle && dest.selectedTitle && result.new_repeats_id) {
            this.loadingMessage = `Updating title for destination ${i + 1}...`
            await ScheduleApi.updateSequenceTitle({
              repeats_id: result.new_repeats_id,
              media_id: dest.selectedTitle.id,
              episode_id: dest.selectedEpisodeId
            })
          }

          if (result) copiedCount++
        }

        if (copiedCount > 0) {
          this.$buefy.toast.open({
            message: `Sequence copied to ${copiedCount} destination${copiedCount !== 1 ? 's' : ''} successfully!`,
            position: 'is-top',
            type: 'is-success',
            duration: 5000
          })
        }

        this.closeModal()
        this.$emit('refresh')
      } catch (error) {
        console.error('Error copying sequence:', error)
        this.$buefy.toast.open({
          message: 'Failed to copy sequence. Please try again.',
          position: 'is-top',
          type: 'is-danger',
          duration: 5000
        })
      } finally {
        this.loading = false
      }
    },
    closeModal() {
      this.showModal = false
      this.additionalSlots = []
      this.primaryChangeTitle = false
      this.primarySelectedTitle = null
      this.primarySelectedEpisodeId = null
      this.primaryTitleSearch = ''
      this.primaryEpisodes = []
      this.$emit('close-modal')
    },
    formatDateTime(date) {
      if (!date) return ''
      return format(new Date(date), 'EEE MMM d @ HH:mm')
    }
  }
}
</script>

<style scoped>
.destination-item {
  margin-bottom: 10px;
  padding: 12px;
}

.destination-item:last-child {
  margin-bottom: 0;
}

.destination-header {
  margin-bottom: 5px;
}

.title-change-section {
  padding-top: 8px;
  border-top: 1px dashed #dbdbdb;
}

.destinations-list {
  padding-right: 5px;
}

/* Ensure autocomplete dropdowns are visible above the scrollable container */
::v-deep .autocomplete .dropdown-menu {
  z-index: 100 !important;
}

::v-deep .dropdown-content {
  max-height: 300px;
  overflow-y: auto;
}
</style>
